import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPaymentRequest } from '../reducers/payReducer';
import {
  fetchInboxThreadView,
  handleSendMessage,
  setInboxCommunicationContext,
  setInboxContext,
} from '../reducers/inboxReducer';
import { setActiveUser } from '../reducers/userReducer';
import * as PayHelpers from '../helpers/PayHelpers';
import { EventHelpers } from '../helpers';
import { ValidationService, ValidationShapers } from '../services/ValidationService';
import { removePeriodsFromString } from '../helpers/StringHelpers';
import { isAndroid, isChromeBrowser } from '../helpers/BrowserHelpers';
import { organizationSelectors } from '../selectors';
import resolveInboxCommunicationContext from '../helpers/InboxCommunicationHelper';
import threadService from '../services/threadService';

const useRhinopayModal = (userId) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [paymentRequestAmount, setPaymentRequestAmount] = useState('');
  const [paymentRequestNote, setPaymentRequestNote] = useState('');
  const [errors, setErrors] = useState({});
  const [isFormInProgress, setIsFormInProgress] = useState(false);
  const activeOrg = useSelector((state) => organizationSelectors.getCurrentOrg(state));
  const organizationId = useSelector((state) => state.auth?.currentOrg);

  const reset = () => {
    setPaymentRequestAmount('');
    setPaymentRequestNote('');
    setErrors({});
    setIsFormInProgress(false);
  };

  const open = () => setIsOpen(true);

  const close = () => {
    reset();
    setIsOpen(false);
  };

  const handleChange = (name, value) => {
    if (name === 'paymentRequestNote') setPaymentRequestNote(value);
  };

  const handleFocus = (event) => {
    if (Number.isNaN(parseFloat(event.target.value))) {
      setPaymentRequestAmount('0.00');
    }
  };

  const shapeNumberToCurrency = (inputValue) => {
    if (Number.isNaN(parseFloat(inputValue))) return '0.00';
    return (parseFloat(inputValue) / 100).toFixed(2);
  };

  const getKeyValue = (keyCode) => {
    let kc = keyCode;
    if (kc > 57) kc -= 48;
    return (kc >= 48 && kc <= 57) ? String.fromCharCode(kc) : null;
  };

  const handleRequestAmountChange = (event) => {
    const DELETE_KEY_CODE = 8;
    const eventKey = event.which || event.keyCode;
    if (isAndroid() && isChromeBrowser()) {
      if (event.type === 'input') {
        setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(event.target.value)));
      }
    } else if (event.type === 'keydown' && eventKey === DELETE_KEY_CODE && paymentRequestAmount.length > 0) {
      const trimmed = paymentRequestAmount.slice(0, paymentRequestAmount.length - 1);
      setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(trimmed)));
      event.preventDefault();
    } else if (event.type === 'keyup' && eventKey !== DELETE_KEY_CODE && getKeyValue(eventKey)) {
      setPaymentRequestAmount(shapeNumberToCurrency(removePeriodsFromString(paymentRequestAmount) + getKeyValue(eventKey)));
    }
  };

  const handleSubmitRequest = async () => {
    const validationErrors = ValidationService(ValidationShapers.shapePaymentRequest({ paymentRequestAmount, paymentRequestNote }));
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsFormInProgress(true);

    // Save current inbox state so we can restore it after
    const savedState = dispatch((_, getState) => {
      const s = getState();
      return {
        userUserId: s.user.userId,
        inboxUserId: s.inbox.userId,
        activeToChannelId: s.inbox.activeToChannelId,
        activeFromChannelId: s.inbox.activeFromChannelId,
        activeSecureFromChannelId: s.inbox.activeSecureFromChannelId,
        activeSecureNotificationToChannelId: s.inbox.activeSecureNotificationToChannelId,
        activeSecureNotificationFromChannelId: s.inbox.activeSecureNotificationFromChannelId,
      };
    });

    try {
      // 1. Create the payment request
      await dispatch(createPaymentRequest({
        orgId: activeOrg.id,
        userId,
        requestAmount: parseFloat(paymentRequestAmount, 10),
        note: paymentRequestNote,
      }));

      // 2. Set user context so getActiveUser / getInboxOptionsInReducer resolve
      dispatch(setActiveUser(userId));
      dispatch(setInboxContext({
        userId,
        groupId: null,
        assigned: false,
        direct: false,
        following: false,
        mentions: false,
      }));

      // 3. Load the thread view (populates threadFromChannelIds)
      await dispatch(fetchInboxThreadView({ userId }));

      // 4. Fetch thread events and resolve channels
      const threadOptions = {
        userId,
        organizationId,
        assigned: false,
        following: false,
        direct: false,
        pageNo: 0,
        channelIds: [],
        initialPageNo: 0,
      };
      await dispatch(threadService.endpoints.getThread.initiate(threadOptions));

      const state = dispatch((_, getState) => getState());
      const threadResult = threadService.endpoints.getThread.select(threadOptions)(state);
      const eventIds = threadResult?.data?.ids || [];
      const entities = threadResult?.data?.entities || {};

      const messageEventIds = eventIds.filter((id) => EventHelpers.isMessageEvent(entities[id]));
      const secureEventIds = eventIds.filter((id) => EventHelpers.isSecureEvent(entities[id]));
      const mostRecentEvent = entities[eventIds[eventIds.length - 1]] || null;
      const mostRecentMessageEvent = EventHelpers.shapeEventToV3(entities[messageEventIds[messageEventIds.length - 1]]);
      const mostRecentSecureEvent = EventHelpers.shapeEventToV3(entities[secureEventIds[secureEventIds.length - 1]]);

      const commContext = resolveInboxCommunicationContext({
        activeUser: state.user.users[userId],
        loggedInUser: state.user.users[state.auth.currentUser],
        userOrganization: activeOrg,
        channels: state.channel.channels,
        groups: state.group.groups,
        threadFromChannelIds: state.inbox.threadFromChannelIds,
        phones: Object.values(state.phone.phones),
        facebooks: state.facebook.facebooks,
        instagrams: state.instagram.instagrams,
        rhinograms: state.rhinogram.rhinograms,
        mostRecentEvent,
        mostRecentMessageEvent,
        mostRecentSecureEvent,
        groupId: null,
        isAssignedInbox: false,
      });

      if (commContext) {
        dispatch(setInboxCommunicationContext(commContext));
      }

      // 5. Send the payment message
      const paymentRequest = state.pay.paymentRequests[userId];
      if (paymentRequest) {
        const messageText = PayHelpers.generatePaymentRequestMessageText(paymentRequest, activeOrg);
        await dispatch(handleSendMessage({ messageText, threadOptions }));
      }

      reset();
      setIsOpen(false);
    } catch (err) {
      reset();
      setIsOpen(false);
    } finally {
      // Restore previous inbox state
      dispatch(setActiveUser(savedState.userUserId));
      dispatch(setInboxContext({
        userId: savedState.inboxUserId,
        groupId: null,
        assigned: false,
        direct: false,
        following: false,
        mentions: false,
      }));
      dispatch(setInboxCommunicationContext({
        activeToChannelId: savedState.activeToChannelId,
        activeFromChannelId: savedState.activeFromChannelId,
        activeSecureFromChannelId: savedState.activeSecureFromChannelId,
        activeSecureNotificationToChannelId: savedState.activeSecureNotificationToChannelId,
        activeSecureNotificationFromChannelId: savedState.activeSecureNotificationFromChannelId,
      }));
    }
  };

  return {
    isOpen,
    open,
    close,
    errors,
    isFormInProgress,
    paymentRequestAmount,
    paymentRequestNote,
    handleChange,
    handleFocus,
    handleRequestAmountChange,
    handleSubmitRequest,
  };
};

export default useRhinopayModal;
